import { useCallback, useMemo, useRef, useState } from "react"
import { cancelBet } from "@/shared/api"
import { toast } from "sonner"
import type { SnapshotBet, ViewPhase } from "@/entities/pvp-roulette"
import { translatePath } from "@/shared/i18n"
import {
  aggregateBetsByUser,
  betKey,
  type AggregatedUserBets,
} from "../lib/playersListUtils"
import { makeSuccessToast } from "@/shared/lib/toast-success"
import { makeErrorToast } from "@/shared/lib/toast-error"

export function usePlayersListData(
  activeBets: SnapshotBet[],
  phase: ViewPhase | null
) {
  const phaseRef = useRef(phase)
  phaseRef.current = phase
  const canCancelPhase = phase === "WAITING"

  const { betsByPartyKey, aggregatedByPartyKey } = useMemo(() => {
    const betsMap = new Map<string, SnapshotBet[]>()
    for (const b of activeBets) {
      const k = betKey(b)
      const list = betsMap.get(k)
      if (list) list.push(b)
      else betsMap.set(k, [b])
    }
    for (const list of betsMap.values()) {
      list.sort((a, b) => {
        const ta = a.createdAt ?? 0
        const tb = b.createdAt ?? 0
        if (ta !== tb) return ta - tb
        return a.betId.localeCompare(b.betId)
      })
    }
    const aggMap = new Map<string, AggregatedUserBets[]>()
    for (const [k, bets] of betsMap) {
      aggMap.set(k, aggregateBetsByUser(bets))
    }
    return { betsByPartyKey: betsMap, aggregatedByPartyKey: aggMap }
  }, [activeBets])

  const [cancellingRowKey, setCancellingRowKey] = useState<string | null>(null)

  const cancelBetsByIds = useCallback(
    async (betIds: string[], rowKey: string) => {
      if (betIds.length === 0) return
      if (phaseRef.current !== "WAITING") return
      setCancellingRowKey(rowKey)
      try {
        await Promise.all(betIds.map((id) => cancelBet(id)))
        makeSuccessToast({
          label:
            betIds.length > 1
              ? translatePath("toast.betsCancelled")
              : translatePath("toast.betCancelled"),
        })
      } catch (err) {
        console.error("[PlayersList] cancelBet failed:", err)
        makeErrorToast({ label: translatePath("toast.failedCancelBet") })
      } finally {
        setCancellingRowKey(null)
      }
    },
    []
  )

  return {
    betsByPartyKey,
    aggregatedByPartyKey,
    cancelBetsByIds,
    cancellingRowKey,
    canCancelPhase,
  }
}
